import { useState } from 'react';
import { useEffect } from 'react';

import { CheckedState } from '@radix-ui/react-checkbox';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CommentSection } from './comments-section';
import { FactDocumentDetails } from './document-details';
import { RelevanceSelector } from './relevance-selector';
import { SubsetFact } from '../../../../../backend/src/types/types';
import { RelevanceOptions } from '../../../constants/filterOptions';
import { useUserContext } from '../../../Contexts/User';
import { ProcessingChip, ProcessingState } from '../processing-chip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../shadcn/dialog';
import { Input } from '../shadcn/input';
import { Label } from '../shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '../shadcn/popover';
import { Skeleton } from '../shadcn/skeleton';
import { Textarea } from '../shadcn/textarea';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteFact } from '@/api/mutations/useDeleteFact';
import { useSetKeyForSubsetFacts } from '@/api/mutations/useSetKeyForSubsetFacts';
import useUpdateFact from '@/api/mutations/useUpdateFact';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { QueryEntity } from '@/api/types';
import { relevanceIcons, relevanceMapping } from '@/helpers/filterNames';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { useSelectedFactsDb } from '@/hooks/useSelectedFactsDb';
import { Chip } from '@/new/components/chip';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';
import { useStore } from '@/store';
import { Relevance } from '@/types';

interface EditFactFieldDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: (value: string) => void;
  originalValue: string;
  buttonClassName?: string;
}

const EditFactFieldDialog = ({
  isOpen,
  onOpenChange,
  title,
  label,
  value,
  onValueChange,
  onSubmit,
  originalValue,
  buttonClassName,
}: EditFactFieldDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          className={cn(
            'group-data-[state=open]:animate-in group-data-[state=open]:fade-in p-0.5 text-gray-400 group-data-[state=closed]:hidden hover:text-gray-800',
            buttonClassName,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Icon className="lucide--pencil" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(value);
            onOpenChange(false);
          }}
        >
          <Label>{label}</Label>
          <Textarea value={value} onChange={(e) => onValueChange(e.target.value)} className="mt-2 min-h-32" />

          {label === 'Fact Description' && (
            <p className="mt-4 flex items-center gap-1.5 text-xs text-gray-500">
              <Icon className="lucide--info" /> Note: This change will be reflected across all Subsets
            </p>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => onOpenChange(false)} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={value === originalValue}>
              Update {label}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface FactDropdownProps {
  fact: SubsetFact;
  reference?: number;
  onViewDocument?: (docId: string, factId: string, pageNumber: number) => void;
  selectable?: {
    isSelected: boolean;
    handleSelect: (v: CheckedState) => void;
  };
  isSelected?: boolean;
  subsetId: string;
  onFactSelectionChange?: (factId: string, selected: boolean) => void;
}

export const FactDropdown = ({
  fact,
  reference,
  onViewDocument,
  selectable,
  isSelected,
  subsetId,
  onFactSelectionChange,
}: FactDropdownProps) => {
  const queryClient = useQueryClient();
  const { matterId } = useParams<{ matterId: string }>();

  const { user } = useUserContext();

  const { openDocViewer } = useDocumentNavigation();
  const { openInconsistencyDrawer } = useStore();

  // Only editable if not a reference
  const editable = reference === undefined;

  const { data: subset } = useGetSubset(matterId, subsetId);

  const { mutate: setKeyForSubsetFacts } = useSetKeyForSubsetFacts(matterId!, subsetId);

  const [relevance, setRelevance] = useState(fact.relevance);

  const [factDateText, setFactDateText] = useState(fact.factDateText ?? '');
  const [isFactDateTextPopoverOpen, setIsFactDateTextPopoverOpen] = useState(false);

  const [factDescription, setFactDescription] = useState(fact.description ?? '');
  const [isFactDescriptionDialogOpen, setIsFactDescriptionDialogOpen] = useState(false);

  const [factSignificance, setFactSignificance] = useState(fact.significance ?? '');
  const [isFactSignificanceDialogOpen, setIsFactSignificanceDialogOpen] = useState(false);

  const { mutate: updateFact } = useUpdateFact(subsetId);

  const onSuccess = () => {
    const { caseId } = fact;
    if (fact.caseId) {
      queryClient.invalidateQueries([QueryEntity.CaseFacts, { caseId }]);
      queryClient.invalidateQueries([QueryEntity.KeyFacts, { caseId }]);
    }
  };

  const handleUpdateRelevance = (newRelevance: Relevance) => {
    setRelevance(newRelevance);
    updateFact(
      { field: 'relevance', value: newRelevance, factId: fact.id },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEventsV2.UpdateFactRelevance);
          onSuccess();
        },
      },
    );
  };

  const handleUpdateSignificance = (newSignificance: string) => {
    updateFact(
      { field: 'significance', value: newSignificance, factId: fact.id },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEventsV2.UpdateFactContent);
          onSuccess();
        },
      },
    );
  };

  const handleUpdateDateText = (newDateText: string) => {
    updateFact(
      { field: 'factDateText', value: newDateText, factId: fact.id },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEventsV2.UpdateFactDate);
          onSuccess();
        },
      },
    );
  };

  const handleUpdateDescription = (newDescription: string) => {
    updateFact(
      { field: 'description', value: newDescription, factId: fact.id },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEventsV2.UpdateFactContent);
          onSuccess();
        },
      },
    );
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { mutate: deleteFact, isLoading: isDeletingFact } = useDeleteFact({
    factId: fact.id,
    caseId: fact.caseId,
  });

  const { deselectFact } = useSelectedFactsDb();

  const handleDeleteFact = () => {
    deleteFact(undefined, {
      onSuccess: () => {
        trackCustomEvent(TrackingEventsV2.DeleteFact);
        setIsDeleteModalOpen(false);

        // Deselect in db and update parent state
        deselectFact(subsetId, fact.id);
        onFactSelectionChange?.(fact.id, false);
      },
    });
  };

  useEffect(() => {
    setRelevance(fact.relevance);
  }, [fact.relevance]);

  return (
    <>
      <AccordionItem
        value={fact.id}
        className={cn(
          'group w-full border-t border-gray-300 px-4 pt-3 not-first:-mt-[1px] not-last:border-b',
          isSelected ? 'z-10 border-teal-300' : 'border-t-transparent',
        )}
      >
        <AccordionTrigger className="w-full pt-1">
          <div className="mr-6 w-full font-normal text-gray-800">
            <div className="mb-1.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {selectable && (
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    className="mr-1"
                    checked={selectable.isSelected}
                    onCheckedChange={selectable.handleSelect}
                  />
                )}

                {user?.organisation.isLabellingOrg && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(fact.id);
                      toast.success('Fact ID copied to clipboard');
                    }}
                  >
                    <Icon className="lucide--copy text-gray-500 duration-150 hover:text-gray-900" />
                  </button>
                )}

                {reference !== undefined && (
                  <span className="rounded-md border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-xs text-blue-500">
                    {reference}
                  </span>
                )}
                <p
                  className={cn(
                    isSelected && 'text-teal-500',
                    'flex items-center gap-2 font-semibold break-all text-gray-700',
                  )}
                >
                  <span className="-mb-0.25 line-clamp-1">{fact.factDateText ?? '(Date Unknown)'}</span>

                  {editable && (
                    <Popover
                      open={isFactDateTextPopoverOpen}
                      onOpenChange={(isOpen) => {
                        setIsFactDateTextPopoverOpen(isOpen);
                        // reset input field when popover opens
                        if (isOpen) {
                          setFactDateText(fact.factDateText ?? '');
                        }
                      }}
                    >
                      <PopoverTrigger onClick={(e) => e.stopPropagation()}>
                        <Icon className="lucide--pencil text-xs text-gray-400 hover:text-gray-800" />
                      </PopoverTrigger>
                      <PopoverContent className="max-w-96" side="right">
                        <p className="text-gray-600">
                          Enter date in any format e.g. 2001, 9th June 2003, 2006-2007, September 2009.
                        </p>
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                          <Icon className="lucide--info" /> Note: This change will be reflected across all Subsets
                        </p>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleUpdateDateText(factDateText);
                            setIsFactDateTextPopoverOpen(false);
                          }}
                          className="mt-3 mb-1 flex gap-1.5"
                        >
                          <Input
                            value={factDateText}
                            onChange={(e) => {
                              setFactDateText(e.target.value);
                            }}
                          />
                          <Button type="submit" disabled={factDateText === (fact.factDateText ?? '')}>
                            <Icon className="lucide--arrow-right" />
                          </Button>
                        </form>
                      </PopoverContent>
                    </Popover>
                  )}
                </p>
              </div>

              <div className="flex items-center justify-end gap-2">
                {fact.isKey === null ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="flex">
                        <Icon className="lucide--loader-2 animate-spin text-gray-500 duration-1500" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Processing key status</TooltipContent>
                  </Tooltip>
                ) : fact.isKey === true ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          trackCustomEvent(TrackingEventsV2.SetKeyForFacts, { isKey: false, subsetId });
                          setKeyForSubsetFacts({ key: false, factIds: [fact.id] });
                        }}
                      >
                        <Chip
                          className={cn(
                            CHIP_COLOURS.key,
                            'mr-0.5 border transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-500',
                          )}
                        >
                          <Icon className="lucide--key-round" />
                          <span>Key</span>
                        </Chip>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Remove from key facts in this subset</TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          trackCustomEvent(TrackingEventsV2.SetKeyForFacts, { isKey: true, subsetId });
                          setKeyForSubsetFacts({ key: true, factIds: [fact.id] });
                        }}
                      >
                        <Icon className="lucide--key-round text-gray-400/80 hover:text-amber-500" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Mark fact as key in this subset</TooltipContent>
                  </Tooltip>
                )}

                {/* For when relevance will become nullable */}
                {relevance ? (
                  <Chip className={cn(CHIP_COLOURS.relevance[relevance], 'border')}>
                    <Icon className={relevanceIcons[relevance]} />
                    {relevanceMapping[relevance]}
                  </Chip>
                ) : (
                  <ProcessingChip processingState={ProcessingState.Processing} processingText="Processing Relevance" />
                )}

                {fact.inconsistencies?.length !== 0 && (
                  <Button
                    variant="ghost"
                    className="p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (fact.inconsistencies) {
                        openInconsistencyDrawer({
                          inconsistencyIds: fact.inconsistencies.map((i) => i.inconsistencyId),
                        });
                      }
                    }}
                  >
                    <Chip className={cn(CHIP_COLOURS.disputed, 'border duration-150 hover:border-red-400')}>
                      <Icon className="lucide--triangle-alert" />
                      <span>Disputed</span>
                    </Chip>
                  </Button>
                )}

                {fact.issues?.length > 0 && (
                  <Popover>
                    <PopoverTrigger onClick={(e) => e.stopPropagation()}>
                      <Chip
                        className={cn(
                          CHIP_COLOURS.issue,
                          'border duration-150 hover:border-blue-400 hover:bg-blue-100/80 hover:text-blue-700',
                        )}
                      >
                        {fact.issues.length} {fact.issues.length === 1 ? 'Issue' : 'Issues'}
                      </Chip>
                    </PopoverTrigger>
                    <PopoverContent side="left" className="w-fit max-w-md">
                      <p className="mb-3 flex items-center gap-1.5 text-gray-600">
                        <Icon className="lucide--group" />
                        Issues
                      </p>
                      <div className="flex w-full flex-wrap gap-1.5">
                        {fact.issues.map((i) => (
                          <Chip key={i} className={cn(CHIP_COLOURS.issue)}>
                            {i}
                          </Chip>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {fact.locations.map((location) => (
                  <div key={location.docId} className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p
                          onClick={(e) => {
                            e.stopPropagation();
                            trackCustomEvent(TrackingEventsV2.OpenDocViewerFromFact);

                            (onViewDocument ?? openDocViewer)(
                              location.docId,
                              fact.id,
                              location.boundingBoxes?.[0]?.pageNumber ?? 0,
                            );
                          }}
                          className="flex max-w-28 cursor-pointer items-center gap-1.25 text-xs break-all text-ellipsis text-blue-600 duration-150 hover:text-blue-700"
                        >
                          <Icon className="lucide--file mb-0.25" />
                          <span className="line-clamp-1">{location.doc?.file.name}</span>
                        </p>
                      </TooltipTrigger>

                      <TooltipContent>{location.doc?.file.name}</TooltipContent>
                    </Tooltip>

                    <Chip className={CHIP_COLOURS.type}>{location.doc?.type}</Chip>
                  </div>
                ))}
              </div>
            </div>

            <p className={`${isSelected ? 'text-gray-900' : 'text-gray-800'} group-data-[state=closed]:line-clamp-1`}>
              {fact.description}

              {editable && (
                <EditFactFieldDialog
                  isOpen={isFactDescriptionDialogOpen}
                  onOpenChange={setIsFactDescriptionDialogOpen}
                  title="Edit Fact Description"
                  label="Fact Description"
                  value={factDescription}
                  onValueChange={setFactDescription}
                  onSubmit={handleUpdateDescription}
                  originalValue={fact.description ?? ''}
                  buttonClassName="ml-1.5 text-xs"
                />
              )}
            </p>
          </div>
        </AccordionTrigger>

        <AccordionContent className="mb-4 flex flex-col gap-4">
          <FactDocumentDetails
            fact={fact}
            gotoDocReference={
              onViewDocument
                ? onViewDocument
                : (docId: string, factId: string, pageNum: number) => {
                    openDocViewer(docId, factId, pageNum);
                    trackCustomEvent(TrackingEventsV2.OpenDocViewerFromFact);
                  }
            }
          />

          <div
            className={cn(
              'relative mt-1.5 flex gap-6 rounded-md border bg-gray-50 px-4 py-3 text-xs',
              !subset?.isAllDocuments && 'pt-9',
            )}
          >
            {/* subset indicator */}
            {!subset?.isAllDocuments && (
              <div className="absolute -top-[1px] left-3 flex items-center gap-1.5 rounded-b-lg border bg-white px-2 py-1 text-xs font-medium text-gray-700">
                <Icon className="lucide--group" />
                <span>{subset?.name ?? ''}</span>
              </div>
            )}

            {/* significance */}
            <div className="flex grow flex-col gap-2">
              <p className="flex items-center gap-2 font-medium">
                <span>{subset?.isAllDocuments ? '' : 'Subset'} Significance</span>

                <TooltipIcon
                  side="right"
                  tooltipContent={`Short analysis of this fact's significance as it pertains to this ${subset?.isAllDocuments ? `matter's` : `subset's`} issues`}
                />

                {editable && (
                  <EditFactFieldDialog
                    isOpen={isFactSignificanceDialogOpen}
                    onOpenChange={setIsFactSignificanceDialogOpen}
                    title="Edit Fact Significance"
                    label="Fact Significance"
                    value={factSignificance ?? ''}
                    onValueChange={setFactSignificance}
                    onSubmit={handleUpdateSignificance}
                    originalValue={fact.significance ?? ''}
                    buttonClassName="text-[10px]"
                  />
                )}
              </p>

              {fact.significance ? (
                <p className="text-gray-700">{fact.significance}</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-9/10 bg-gray-200" />
                  <Skeleton className="h-4 w-2/10 bg-gray-200" />
                </div>
              )}
            </div>

            <div className="flex min-w-54 flex-col gap-2">
              <p className="flex items-center gap-2 pl-0.5 font-medium">
                {subset?.isAllDocuments ? '' : 'Subset'} Relevance
                <TooltipIcon
                  side="right"
                  tooltipContent={`Is this fact relevant to the ${subset?.isAllDocuments ? 'matter' : 'subset'} issues?`}
                />
              </p>

              {fact.relevance ? (
                editable ? (
                  <RelevanceSelector relevanceText={fact.relevance} updateRelevance={handleUpdateRelevance} />
                ) : (
                  <p className="pl-0.5">{RelevanceOptions[fact.relevance].label}</p>
                )
              ) : (
                <ProcessingChip processingState={ProcessingState.Processing} processingText="Processing Relevance" />
              )}
            </div>
          </div>

          <div className="relative px-1">
            <CommentSection fact={fact} />

            <Button className="absolute top-0 right-0" onClick={() => setIsDeleteModalOpen(true)} size="sm">
              <Icon className="lucide--archive" />
              <span>Archive Fact</span>
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onConfirm={handleDeleteFact}
        isLoading={isDeletingFact}
        title="Archive Fact"
      >
        <p>Are you sure? This will remove this fact from every subset.</p>
      </ConfirmationModal>
    </>
  );
};
